// A site's company profile: the broad picture of the business the assistant is
// always given, whatever the visitor asks and whether or not the knowledge base
// has a matching excerpt. Admins fill named fields instead of writing a prompt;
// formatCompanyProfile turns them into one prompt section. Written by site
// admins, so it is trusted like the site's own system prompt (it sits before
// the injection guard, see server/src/ai/prompt-guard.ts). Pure.

export interface CompanyProfile {
  /** Business name as visitors know it. */
  name?: string | null;
  /** What the company is and does, in a paragraph or two. */
  about?: string | null;
  /** Products or services offered. */
  offering?: string | null;
  /** Who the customers are. */
  audience?: string | null;
  /** Policies visitors ask about: shipping, refunds, pricing, support hours. */
  policies?: string | null;
  /** How customers reach a person: phone, email, address, hours. */
  contact?: string | null;
  /** Main website, for "read more" pointers. */
  website?: string | null;
  /** Voice of the answers. */
  tone?: CompanyTone | null;
}

export type CompanyTone = 'friendly' | 'professional' | 'concise' | 'playful';

export const COMPANY_TONES: Record<CompanyTone, string> = {
  friendly: 'Warm and friendly, plain words, no jargon.',
  professional: 'Professional and precise; courteous without being casual.',
  concise: 'As short as an answer can be while still complete.',
  playful: 'Light and upbeat, with the odd bit of humour; never at the visitor’s expense.',
};

/** Field limits, shared by the admin form and the API schema. */
export const COMPANY_PROFILE_LIMITS = {
  name: 120,
  website: 300,
  text: 4000,
} as const;

const SECTIONS: Array<[keyof CompanyProfile, string]> = [
  ['about', 'About'],
  ['offering', 'Products and services'],
  ['audience', 'Customers'],
  ['policies', 'Policies'],
  ['contact', 'Contact'],
];

const clean = (v: string | null | undefined) => (v ?? '').replace(/\r\n?/g, '\n').trim();

/** True when the profile holds anything worth putting in a prompt. */
export function hasCompanyProfile(p: CompanyProfile | null | undefined): boolean {
  if (!p) return false;
  return [p.name, p.website, ...SECTIONS.map(([k]) => p[k] as string | null | undefined)].some((v) => clean(v)) || !!p.tone;
}

/** The profile as a system-prompt section, or '' when it is empty. */
export function formatCompanyProfile(p: CompanyProfile | null | undefined): string {
  if (!p || !hasCompanyProfile(p)) return '';
  const lines: string[] = ['## The company you represent'];
  const name = clean(p.name);
  const website = clean(p.website);
  if (name) lines.push(`You are the assistant for ${name}.`);
  if (website) lines.push(`Website: ${website}`);
  for (const [key, heading] of SECTIONS) {
    const text = clean(p[key] as string | null | undefined);
    if (text) lines.push('', `### ${heading}`, text);
  }
  if (p.tone && COMPANY_TONES[p.tone]) lines.push('', `### Tone`, COMPANY_TONES[p.tone]);
  lines.push(
    '',
    'Answer from this profile and the knowledge base. When neither covers the question, say so plainly and offer to connect the visitor with the team.',
  );
  return lines.join('\n');
}

/**
 * The trusted head of the system prompt: the site's own instructions, then
 * its company profile. Either may be empty; the default prompt stands in for
 * missing instructions.
 */
export function siteInstructions(
  systemPrompt: string | null | undefined,
  profile: CompanyProfile | null | undefined,
  defaultPrompt: string,
): string {
  const base = clean(systemPrompt) || defaultPrompt;
  const company = formatCompanyProfile(profile);
  return company ? `${base}\n\n${company}` : base;
}
